/**
 * Browser WASM Bridge Adapter.
 * Uses .NET browser-wasm with [JSExport] for direct JavaScript interop.
 *
 * The dotnet runtime files must be served as static assets (not bundled).
 * They are loaded via script injection to avoid bundler processing.
 *
 * Database is imported/exported as a complete unit - no schema generation.
 */

import { KuikaAppManager } from "../shared/utilty/kuika-app-manager";
import { IKuikaCoreWasmBridgeAdapter, IKuikaCoreWasmMethods } from "./kuika-core-wasm-bridge-adapter";

declare let window: any;

// Type for browser-wasm dotnet runtime exports (uses sync interface)
interface DotnetExports {
  KuikaCoreExports: IKuikaCoreWasmMethods;
}

interface DotnetRuntime {
  getAssemblyExports: (assemblyName: string) => Promise<DotnetExports>;
}

interface DotnetApi {
  dotnet: {
    create: () => Promise<DotnetRuntime>;
  };
}

export class BrowserBridgeAdapter implements IKuikaCoreWasmBridgeAdapter {
  private initPromise: Promise<void> | null = null;

  private isInitialized = false;

  private exports: DotnetExports | null = null;

  private wasmBaseUrl = "";

  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    // Prevent multiple concurrent initializations
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.doInitialize();
    await this.initPromise;
  }

  private async doInitialize(): Promise<void> {
    try {
      console.log("BrowserBridgeAdapter: Initializing...");

      // Determine the base URL for WASM files (they're served as static assets)
      this.wasmBaseUrl = `${KuikaAppManager.GetFrontEndUrl()}/wasm/kuika-dotnet-core/browser`;

      // Load the dotnet runtime via dynamic import from URL
      // This bypasses the bundler and loads directly from the served files
      const dotnetUrl = `${this.wasmBaseUrl}/_framework/dotnet.js`;

      console.log(`BrowserBridgeAdapter: Loading dotnet from ${dotnetUrl}`);

      // Use import() with the full URL to bypass bundler
      const dotnetModule = (await import(/* webpackIgnore: true */ dotnetUrl)) as DotnetApi;

      // Create the runtime
      const runtime = await dotnetModule.dotnet.create();

      // Get the exported methods from our assembly
      this.exports = await runtime.getAssemblyExports("KuikaCoreWasm.Browser");

      this.isInitialized = true;
      console.log("BrowserBridgeAdapter: Initialized successfully");
    } catch (error) {
      console.error("BrowserBridgeAdapter: Failed to initialize", error);
      this.initPromise = null;
      throw error;
    }
  }

  getIsInitialized(): boolean {
    return this.isInitialized;
  }

  getAdapterType(): "browser" | "electron" {
    return "browser";
  }

  private ensureExports(): IKuikaCoreWasmMethods {
    if (!this.exports) {
      throw new Error("WASM runtime not properly initialized");
    }
    return this.exports.KuikaCoreExports;
  }

  // Core methods
  async Add(a: number, b: number): Promise<number> {
    await this.initialize();
    return this.ensureExports().Add(a, b);
  }

  async Ping(): Promise<string> {
    await this.initialize();
    return this.ensureExports().Ping();
  }

  // Database lifecycle methods
  async Db_Initialize(): Promise<void> {
    await this.initialize();
    return this.ensureExports().Db_Initialize();
  }

  async Db_IsInitialized(): Promise<boolean> {
    await this.initialize();
    return this.ensureExports().Db_IsInitialized();
  }

  async Db_Close(): Promise<void> {
    await this.initialize();
    return this.ensureExports().Db_Close();
  }

  // Database import/export methods
  async Db_ExportDatabaseBase64(): Promise<string> {
    await this.initialize();
    return this.ensureExports().Db_ExportDatabaseBase64();
  }

  async Db_ImportDatabaseBase64(base64Data: string): Promise<void> {
    await this.initialize();
    return this.ensureExports().Db_ImportDatabaseBase64(base64Data);
  }

  // Sync methods
  async Db_GetPendingChanges(): Promise<string> {
    await this.initialize();
    return this.ensureExports().Db_GetPendingChanges();
  }

  async Db_MarkAsSynced(tableRecordsJson: string): Promise<void> {
    await this.initialize();
    return this.ensureExports().Db_MarkAsSynced(tableRecordsJson);
  }

  async Db_GetOfflineTableSettings(): Promise<string> {
    await this.initialize();
    return this.ensureExports().Db_GetOfflineTableSettings();
  }

  async Db_GetUnsentRecordsBase64(): Promise<string> {
    await this.initialize();
    return this.ensureExports().Db_GetUnsentRecordsBase64();
  }

  async Db_MarkAsSyncedWithCriteria(): Promise<void> {
    await this.initialize();
    return this.ensureExports().Db_MarkAsSyncedWithCriteria();
  }

  async Db_MarkPayloadAsSynced(payloadBase64: string): Promise<void> {
    await this.initialize();
    return this.ensureExports().Db_MarkPayloadAsSynced(payloadBase64);
  }

  // Device metadata methods
  async Db_ImportDatabaseBase64WithReset(base64Data: string): Promise<void> {
    await this.initialize();
    return this.ensureExports().Db_ImportDatabaseBase64WithReset(base64Data);
  }

  async Db_UpdateDeviceMetadata(
    deviceId: string | null,
    deviceName: string | null,
    bundleId: string | null,
    projectName: string | null
  ): Promise<void> {
    await this.initialize();
    return this.ensureExports().Db_UpdateDeviceMetadata(deviceId, deviceName, bundleId, projectName);
  }

  async Db_GetDeviceMetadata(): Promise<string> {
    await this.initialize();
    return this.ensureExports().Db_GetDeviceMetadata();
  }

  // table-browser-adapter-methods-placeholder
}
